import type { LoginDto } from '@nestjs-boilerplate/types';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner';

import { setAuthState } from '@/stores/auth.store';

import { login } from '../api/auth.api';

export function useLogin(redirectTo?: string) {
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (data: LoginDto) => login(data),
    onSuccess: (response) => {
      setAuthState(response.accessToken, response.user);
      toast.success('Login successful!');
      // Email verification gates account-setup and the rest of the app.
      // If the user was bounced out of /verify-email?token=... for login, we
      // preserve THAT URL (and only that URL) so the token survives and they
      // can complete verification. Any other redirectTo is ignored for
      // unverified users so they can't skip the gate.
      const preservesToken =
        typeof redirectTo === 'string' && redirectTo.startsWith('/verify-email');
      // Everyone lands on the dashboard, administrators included. An admin is a
      // normal user of the product first and reaches the admin section from the
      // header when they want it, rather than being dropped into it and having
      // to navigate out.
      const destination = !response.user.emailVerified
        ? preservesToken
          ? redirectTo
          : '/verify-email'
        : !response.user.accountSetupComplete
          ? '/account-setup'
          : redirectTo || '/';
      navigate({ to: destination });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Login failed. Please try again.');
    },
  });
}
